// The six heal beams a world theme can pick (R827, `ui-map-interface.md`
// §6.1 and §6.1.2).
//
// A Tender throws one beam to the hull it tends. `breathing-line` is the
// beam the map shipped with (R725); the five beside it are mockup round
// thirty-four cards 01, 03, 04, 05 and 07, and a player picks one in the
// theme workshop. A value REPLACES the AUTO drawing; it never stacks on it.
//
// This is a leaf (`leaves.test.ts`): it holds the shape of each beam and
// takes every figure from `gen/presentation.ts`. It names the sinks of a
// layer through a type-only clause, the way `chart-glyphs.ts` does, so the
// bundle carries no engine behind it. The engine's additive flag comes in
// on the context for the same reason: a leaf must not import `fast/layer`.
//
// Three rules hold for EVERY value, so a caller states them once and no
// entry below repeats them:
//
//   - **The ink.** The colour is the Tender's, resolved by `inkOf` against
//     `presentation.fleets.tender.beam_ink`. An entry takes `ctx.color`.
//   - **The fainter end.** Every alpha is scaled by `ctx.ends`, the lesser
//     of the two ends' fades, so a beam to a contact that fades in fades in
//     with it.
//   - **The cull.** A beam whose two ends are both off the glass draws
//     nothing. The layer gates that; an entry draws what it is asked for.
//
// **No entry allocates.** The layer owns ONE context object and rewrites it
// per beam, the arrays of the two-strand walk are module constants, and the
// breath is read into a caller-owned `Breath` (§10.1).

import { CONTROL_BEAM_TIERS, FLEET_BEAM, FLEET_TENDER } from '../gen/presentation';
import { breath, type Breath } from './tender';
import { TAU, frac } from './recipe-kit';
import type { BeamStyle } from '../ui/settings';
import type {
  CircleSink, QuadSink, SegmentSink, SpriteEntry,
} from '../fast/layers/batches';

/** What one beam is drawn against. The layer owns it and rewrites it. */
export interface BeamCtx {
  /** The Tender's place, in world px. */
  ax: number;
  ay: number;
  /** The patient's place, in world px. */
  bx: number;
  by: number;
  /** The layer's clock, in seconds. */
  t: number;
  /** The ink the beam wears, already resolved by `inkOf`. */
  color: number;
  /** The fainter of the two ends' fades. Every alpha is scaled by it. */
  ends: number;
  segs: SegmentSink;
  quads: QuadSink;
  rings: CircleSink;
  /** The 32 px radial cell of the control layer, tinted per beam. */
  pip: SpriteEntry;
  /**
   * The engine's additive-blend flag (`fast/layer.ts` `F_ADD`), handed in
   * so this leaf takes no engine import — the rule `inkOf` follows for the
   * faction palette.
   */
  add: number;
  /** The figures. A test hands in its own pack to pin a bound. */
  f: typeof FLEET_BEAM;
  /** The breath is read into this, so a frame allocates nothing. */
  breathOut: Breath;
}

/**
 * The scale that draws the `pip` cell at radius `r` in world px.
 *
 * The cell is square and centred, so its own box width is the diameter at
 * scale 1. The scale is derived rather than stated, because the cell's size
 * is `presentation.control.pip.tex_size` and a figure must not be copied.
 *
 * `recipe-kit.ts cellScale` differs at `entry.w === 0` alone, which no
 * atlas cell reaches, so the two stay apart and each keeps its own answer.
 */
export function pipScale(entry: SpriteEntry, r: number): number {
  return (2 * r) / (entry.w || 1);
}

/**
 * Where a walking dash pattern starts, and how long its lead piece is.
 *
 * The segment batch evaluates the dash from the instance's own p0
 * (`gfx/segments.ts`), so it carries no phase. A walk therefore moves the
 * START of the dashed run forward by `phase` and fills the piece that was
 * cut off the front with one solid line.
 *
 * With a period of `on + off` and a phase `ph` inside it, the run that
 * begins at `ph` carries the pattern the eye expects everywhere past `ph`.
 * Before `ph` the pattern is the PREVIOUS period's tail: it is off while
 * `ph <= off`, and on for `ph - off` px once the phase has walked past the
 * gap. That length is the lead.
 */
export function dashWalk(
  on: number, off: number, phase: number, out: DashWalk = { start: 0, lead: 0 },
): DashWalk {
  const period = on + off;
  const ph = period > 0 ? phase - Math.floor(phase / period) * period : 0;
  out.start = ph;
  out.lead = period > 0 ? Math.max(0, ph - off) : 0;
  return out;
}

/** What `dashWalk` answers. A caller of the frame hands in its own (R131). */
export interface DashWalk { start: number; lead: number }

/** The reused answer of `walkDash`, so a walking beam allocates nothing. */
const WALK: DashWalk = { start: 0, lead: 0 };

/**
 * One dashed line whose dashes walk from `a` to `b` at `phase` px.
 *
 * It writes the dashed run and, when the phase has walked past the gap, one
 * solid lead piece in front of it — at most two instances. It returns how
 * many it wrote.
 */
export function walkDash(
  segs: SegmentSink,
  ax: number, ay: number, bx: number, by: number,
  on: number, off: number, phase: number,
  width: number, color: number, alpha: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return 0;
  const ux = dx / len;
  const uy = dy / len;
  const { start, lead } = dashWalk(on, off, phase, WALK);
  let n = 0;
  const head = Math.min(lead, len);
  if (head > 1e-6) {
    segs.line(ax, ay, ax + ux * head, ay + uy * head, width, color, alpha);
    n++;
  }
  if (start < len) {
    segs.line(ax + ux * start, ay + uy * start, bx, by, width, color, alpha, 0, on, off);
    n++;
  }
  return n;
}

/**
 * A sine polyline along the beam, written as one instance a step.
 *
 * `maxSegments` is the cap of §10.1: the step widens to `len / maxSegments`
 * on a long run, so the count never grows with the reach. It returns how
 * many instances it wrote, which is never over `maxSegments`.
 */
export function packWave(
  b: BeamCtx, waveLen: number, amp: number, phase: number,
  stepPx: number, maxSegments: number,
  width: number, alpha: number, flags: number,
): number {
  const dx = b.bx - b.ax;
  const dy = b.by - b.ay;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return 0;
  const ux = dx / len;
  const uy = dy / len;
  const nx = -uy;
  const ny = ux;
  const step = Math.max(stepPx, len / maxSegments);
  const n = Math.max(1, Math.ceil(len / step));
  let px = 0;
  let py = 0;
  for (let i = 0; i <= n; i++) {
    const s = (len * i) / n;
    const o = Math.sin((s / waveLen) * TAU + phase) * amp;
    const x = b.ax + ux * s + nx * o;
    const y = b.ay + uy * s + ny * o;
    if (i > 0) b.segs.line(px, py, x, y, width, b.color, alpha, flags);
    px = x;
    py = y;
  }
  return n;
}

/** The two strands of the twisted pair, a half turn apart. */
const STRAND_PHASES = [0, Math.PI];

/**
 * One entry a value of `beam_styles`, keyed by the id the player picks.
 *
 * The record is exhaustive over `BeamStyle`, so a new id in
 * `labels.toml` fails the type check here until it has a drawing.
 */
export const BEAM_STYLES: Record<BeamStyle, (b: BeamCtx) => void> = {
  // The beam the map shipped with (R725): the beacon beam of §6.5 stroke
  // for stroke, on an additive blend, breathing as one.
  'breathing-line': (b) => {
    const t = FLEET_TENDER.beam;
    const br = breath(
      b.t, t.pulseHz, t.alphaBase, t.alphaSwing, t.widthBase, t.widthSwing, b.breathOut,
    );
    for (const tier of CONTROL_BEAM_TIERS) {
      b.segs.line(
        b.ax, b.ay, b.bx, b.by,
        tier.width * br.width, b.color, tier.alpha * br.alpha * b.ends, b.add,
      );
    }
  },

  // Card 01: the sov beam's own vocabulary — a faint line, and one pooled
  // pip that slides to the patient. The quietest beam of the six.
  'pip-cable': (b) => {
    const f = b.f['pip-cable'];
    b.segs.line(b.ax, b.ay, b.bx, b.by, f.lineW, b.color, f.lineAlpha * b.ends);
    const u = frac(b.t / f.periodSec);
    b.quads.sprite(
      b.ax + (b.bx - b.ax) * u, b.ay + (b.by - b.ay) * u,
      0, b.pip, b.color, f.pipAlpha * b.ends, pipScale(b.pip, f.pipR),
    );
  },

  // Card 03: the mission lane of §6.1, with the dashes walking toward the
  // patient. It reads as a conveyor, and the speed says how the heal runs.
  'marching-dash': (b) => {
    const f = b.f['marching-dash'];
    walkDash(
      b.segs, b.ax, b.ay, b.bx, b.by,
      f.on, f.off, b.t * f.speedPxPerSec, f.w, b.color, f.alpha * b.ends,
    );
  },

  // Card 04: the chart furniture's cable (§17.3.29) in the heal ink — a
  // sine polyline with a pip and a hard disc walking it.
  cable: (b) => {
    const f = b.f.cable;
    packWave(
      b, f.waveLen, f.amp, -b.t * f.phaseRadPerSec, f.stepPx, f.maxSegments,
      f.w, f.alpha * b.ends, 0,
    );
    const u = frac(b.t / f.periodSec);
    const x = b.ax + (b.bx - b.ax) * u;
    const y = b.ay + (b.by - b.ay) * u;
    b.quads.sprite(x, y, 0, b.pip, b.color, f.pipAlpha * b.ends, pipScale(b.pip, f.pipR));
    b.rings.disc(x, y, f.dotR, b.color, f.dotAlpha * b.ends);
  },

  // Card 05: no line. A ring is born off the patient and closes onto the
  // hull, so inward reads as repair where the splash ring's outward reads
  // as damage (§6.3).
  'gathering-ripple': (b) => {
    const f = b.f['gathering-ripple'];
    for (let k = 0; k < f.rings; k++) {
      const u = frac(b.t / f.periodSec + k / f.rings);
      b.rings.ring(
        b.bx, b.by,
        f.r0 + (f.r1 - f.r0) * u,
        f.w0 + (f.w1 - f.w0) * u,
        b.color,
        (f.alpha0 + (f.alpha1 - f.alpha0) * u) * b.ends,
      );
    }
  },

  // Card 07: two additive strands wound about the axis. Where they cross
  // the blend makes a bead, so a row of beads walks the beam.
  'twisted-pair': (b) => {
    const f = b.f['twisted-pair'];
    for (const ph of STRAND_PHASES) {
      packWave(
        b, f.waveLen, f.amp, -b.t * f.rateRadPerSec + ph, f.stepPx, f.maxSegments,
        f.w, f.alpha * b.ends, b.add,
      );
    }
  },
};
